<template>
  <!-- Mobile toggle button -->
  <button @click="open = !open"
          class="md:hidden fixed bottom-4 right-4 z-50 w-12 h-12 bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-800 transition">
    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
    </svg>
    <span v-if="totalUnread > 0"
          class="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full">
      {{ totalUnread > 9 ? '9+' : totalUnread }}
    </span>
  </button>

  <!-- Sidebar panel -->
  <div :class="['fixed top-0 right-0 h-full z-40 transition-transform duration-300',
                open ? 'translate-x-0' : 'translate-x-full md:translate-x-0']">
    <!-- Backdrop (mobile) -->
    <div v-if="open" class="fixed inset-0 bg-black/30 md:hidden" @click="open = false"></div>

    <aside class="relative w-64 h-full bg-white border-l border-gray-200 shadow-lg flex flex-col"
           style="margin-top: 0;">
      <!-- Close button (mobile) -->
      <button @click="open = false" class="md:hidden absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl z-10">&times;</button>

      <!-- CHAT VIEW -->
      <template v-if="chatUser">
        <!-- Chat header -->
        <div class="flex items-center gap-2 px-3 py-3 border-b border-gray-200 shrink-0">
          <button @click="chatUser = null" class="text-gray-400 hover:text-gray-600">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <span class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-xs font-medium text-blue-800 shrink-0">
            {{ chatUser.name?.charAt(0)?.toUpperCase() || '?' }}
          </span>
          <span class="text-sm font-semibold text-gray-900 truncate">{{ chatUser.name }}</span>
        </div>

        <!-- Chat messages -->
        <div ref="messagesContainer" class="flex-1 overflow-y-auto px-3 py-3 space-y-2">
          <div v-if="chatLoading" class="flex justify-center py-4">
            <div class="w-5 h-5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
          </div>
          <template v-else>
            <p v-if="chatMessages.length === 0" class="text-center text-gray-400 text-xs py-4">{{ t('sidebar.no_messages_yet') }}</p>
            <div v-for="msg in chatMessages" :key="msg.id"
                 :class="['flex', msg.user.id === userId ? 'justify-end' : 'justify-start']">
              <div :class="['max-w-[85%] rounded-xl px-3 py-1.5',
                            msg.user.id === userId ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900']">
                <p class="text-sm whitespace-pre-line break-words">{{ msg.body }}</p>
                <p :class="['text-[10px] mt-0.5', msg.user.id === userId ? 'text-blue-200' : 'text-gray-400']">
                  {{ formatTime(msg.created_at) }}
                </p>
              </div>
            </div>
          </template>
        </div>

        <!-- Chat input -->
        <div class="px-3 py-2 border-t border-gray-200 shrink-0">
          <form @submit.prevent="sendMessage" class="flex gap-1.5">
            <input ref="chatInput" v-model="newMessage" type="text" :placeholder="t('sidebar.type_message')" required
                   class="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <button type="submit" :disabled="!newMessage.trim()"
                    class="px-2.5 py-1.5 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50 shrink-0">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
              </svg>
            </button>
          </form>
        </div>
      </template>

      <!-- USER LIST VIEW -->
      <template v-else>
        <div class="flex-1 overflow-y-auto">
          <!-- Online Users -->
          <div class="px-4 pt-4 pb-2">
            <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {{ t('sidebar.online') }}
              <span class="text-green-500 ml-1">●</span>
            </h3>
            <div v-if="onlineUsers.length === 0" class="text-xs text-gray-400 py-1">{{ t('sidebar.no_online') }}</div>
            <button v-for="user in onlineUsers" :key="'online-' + user.id"
                    @click="openChat(user)"
                    class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition text-left">
              <span class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-xs font-medium text-blue-800 shrink-0">
                {{ user.name?.charAt(0)?.toUpperCase() || '?' }}
              </span>
              <span class="text-sm text-gray-700 truncate">{{ user.name }}</span>
              <span class="w-2 h-2 rounded-full bg-green-500 shrink-0 ml-auto"></span>
            </button>
          </div>

          <hr class="border-gray-100 mx-4" />

          <!-- Unread Messages -->
          <div class="px-4 pt-2 pb-4">
            <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {{ t('sidebar.unread') }}
            </h3>
            <div v-if="unreadConversations.length === 0" class="text-xs text-gray-400 py-1">{{ t('sidebar.no_unread') }}</div>
            <button v-for="conv in unreadConversations" :key="'unread-' + conv.id"
                    @click="openChat(conv.other_user)"
                    class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition text-left">
              <span class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-xs font-medium text-blue-800 shrink-0">
                {{ conv.other_user?.name?.charAt(0)?.toUpperCase() || '?' }}
              </span>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-900 truncate">{{ conv.other_user?.name }}</span>
                  <span class="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full shrink-0 ml-1">
                    {{ conv.unread_count }}
                  </span>
                </div>
                <p class="text-xs text-gray-400 truncate">{{ conv.last_message }}</p>
              </div>
            </button>
          </div>

          <template v-if="recentConversations.length > 0">
            <hr class="border-gray-100 mx-4" />

            <!-- Recent Conversations (offline users) -->
            <div class="px-4 pt-2 pb-4">
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                {{ t('sidebar.recent') }}
              </h3>
              <button v-for="conv in recentConversations" :key="'recent-' + conv.id"
                      @click="openChat(conv.other_user)"
                      class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition text-left">
                <span class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-xs font-medium text-gray-600 shrink-0">
                  {{ conv.other_user?.name?.charAt(0)?.toUpperCase() || '?' }}
                </span>
                <div class="flex-1 min-w-0">
                  <span class="text-sm text-gray-700 truncate block">{{ conv.other_user?.name }}</span>
                  <p v-if="conv.last_message" class="text-xs text-gray-400 truncate">
                    <span v-if="conv.last_message.user_id === userId" class="text-gray-300">{{ t('sidebar.you') }}: </span>
                    {{ conv.last_message.body }}
                  </p>
                </div>
                <span class="w-2 h-2 rounded-full bg-gray-300 shrink-0 ml-auto"></span>
              </button>
            </div>
          </template>
        </div>
      </template>
    </aside>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({ signedIn: Boolean, userId: Number, csrfToken: String })
const { t } = useI18n()

const open = ref(false)
const onlineUsers = ref([])
const unreadConversations = ref([])
const recentConversations = ref([])
const chatUser = ref(null)
const chatMessages = ref([])
const chatLoading = ref(false)
const chatConversationId = ref(null)
const newMessage = ref('')
const messagesContainer = ref(null)
const chatInput = ref(null)
let pollInterval = null
let chatPollInterval = null

const totalUnread = computed(() => unreadConversations.value.reduce((sum, c) => sum + c.unread_count, 0))

function formatTime(d) {
  const date = new Date(d)
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

async function request(url, method, body = null) {
  const o = { method, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' } }
  if (body) o.body = JSON.stringify(body)
  return fetch(url, o)
}

async function openChat(user) {
  if (!user) return
  chatUser.value = user
  chatMessages.value = []
  chatLoading.value = true
  chatConversationId.value = null
  newMessage.value = ''

  try {
    const res = await request('/conversations', 'POST', { recipient_id: user.id })
    if (res.ok) {
      const data = await res.json()
      chatConversationId.value = data.id
      await pollChatMessages()
    }
  } catch {}
  chatLoading.value = false
  scrollToBottom()
  nextTick(() => { if (chatInput.value) chatInput.value.focus() })

  // Start polling for new messages
  if (chatPollInterval) clearInterval(chatPollInterval)
  chatPollInterval = setInterval(pollChatMessages, 3000)
}

async function pollChatMessages() {
  if (!chatConversationId.value) return
  try {
    const res = await fetch(`/conversations/${chatConversationId.value}.json`, {
      headers: { 'Accept': 'application/json', 'X-CSRF-Token': props.csrfToken }
    })
    if (res.ok) {
      const data = await res.json()
      const newMsgs = data.messages || []
      if (newMsgs.length > chatMessages.value.length) {
        chatMessages.value = newMsgs
        scrollToBottom()
      }
    }
  } catch {}
}

async function sendMessage() {
  if (!newMessage.value.trim() || !chatConversationId.value) return
  try {
    const res = await request(`/conversations/${chatConversationId.value}/messages`, 'POST', { message: { body: newMessage.value } })
    if (res.ok) {
      chatMessages.value.push(await res.json())
      newMessage.value = ''
      scrollToBottom()
    }
  } catch {}
}

// Stop chat polling when closing chat
watch(chatUser, (val) => {
  if (!val && chatPollInterval) {
    clearInterval(chatPollInterval)
    chatPollInterval = null
  }
})

async function fetchSidebarData() {
  try {
    const res = await fetch('/sidebar_data', { headers: { 'Accept': 'application/json' } })
    if (res.ok) {
      const data = await res.json()
      onlineUsers.value = data.online_users
      unreadConversations.value = data.unread_conversations
      recentConversations.value = data.recent_conversations || []
    }
  } catch {}
}

function handleOpenChat(e) {
  const user = e.detail
  if (user && user.id && user.id !== props.userId) {
    open.value = true
    openChat(user)
  }
}

function handleToggle() {
  open.value = !open.value
}

onMounted(() => {
  if (props.signedIn) {
    fetchSidebarData()
    pollInterval = setInterval(fetchSidebarData, 30000)
    window.addEventListener('sidebar:open-chat', handleOpenChat)
    window.addEventListener('sidebar:toggle', handleToggle)
  }
})

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
  if (chatPollInterval) clearInterval(chatPollInterval)
  window.removeEventListener('sidebar:open-chat', handleOpenChat)
  window.removeEventListener('sidebar:toggle', handleToggle)
})
</script>
